export const privacyPolicyContent = {
  en: {
    heroTitle: "Revimo Privacy Notice — Testing Phase",
    heroLead:
      "This privacy notice explains how Revimo processes personal data during the temporary testing of the Revimo physiotherapy platform. Revimo is currently being tested and is not intended to replace professional medical advice or a complete clinical record system.",
    lastUpdatedLabel: "Last updated:",
    lastUpdatedValue: "13. 8. 2026",
    sections: [
      {
        id: "controller",
        eyebrow: "Section 1",
        title: "1. Who is responsible for your data?",
        text: `
The data controller is:

Revimo
Email: [info@revimo.eu](mailto:info@revimo.eu)

For privacy questions or requests, contact us at [privacy@revimo.eu](mailto:privacy@revimo.eu).
        `,
      },
      {
        id: "data",
        eyebrow: "Section 2",
        title: "2. Data we collect",
        text: `
1. Patients

During testing, patients may provide or generate:

Email address, which must be a functioning email address for account access and verification.
Display name or nickname. A patient may use a nickname or fictional name if a real name is not needed for the test.
Account login and authentication information.
Physiotherapy plans, exercises, tasks, progress information, or other information entered into or generated by the platform.
Information about which physiotherapist is connected with or has assigned a plan to the patient.
Records of actions performed in the platform, such as plans created, assigned, viewed, edited, or completed.
Technical information required to operate and secure the service, such as timestamps, IP address, browser/device information, and error or security logs, where collected.

Patients should not enter real medical information, diagnoses, injury details, photographs, or other sensitive information unless Revimo specifically instructs them to do so for the test.

2. Physiotherapists

Physiotherapists may provide:

Real name and surname.
Work email address.
Username.
Login and authentication information.
Records of plans created, assigned, edited, or managed.
Information showing which plans were assigned to which patient account.
Technical, security, and activity-log information required to operate and protect the platform.

A telephone number is not required for the current testing phase.
        `,
      },
      {
        id: "use",
        eyebrow: "Section 3",
        title: "3. How we use the data",
        text: `
We use the collected data to:

Create and manage test accounts.
Authenticate users and provide access to Revimo.
Allow physiotherapists to create and assign physiotherapy plans.
Allow patients to view and complete assigned plans.
Record activity necessary to understand how the platform is used and to maintain an accurate history of actions.
Test, troubleshoot, secure, and improve Revimo.
Communicate with users about the testing phase, account access, or important service issues.
Detect, investigate, and prevent unauthorised access, misuse, or security incidents.
Comply with applicable legal obligations where required.

Revimo does not currently provide direct messaging between patients and physiotherapists. Consequently, Revimo does not currently collect or store messages exchanged through the platform.
        `,
      },
      {
        id: "legal",
        eyebrow: "Section 4",
        title: "4. Legal basis",
        text: `
For this temporary testing phase, Revimo processes personal data on the following legal bases under Article 6 GDPR:
- performance of an agreement (Article 6(1)(b)) to create and manage your Revimo account and provide the physiotherapy‑management services you request;
- our legitimate interests (Article 6(1)(f)) in operating, securing and improving Revimo, including logging access, detecting misuse and performing technical tests; and
- your consent (Article 6(1)(a)), where you choose to receive optional communications or where we ask you to agree to additional uses that are not necessary for the core service.

Where you choose to enter information that reveals your health status (for example, details of an injury or treatment plan notes), we process that information based on your explicit consent under Article 9(2)(a) GDPR, for the specified purpose of providing and documenting physiotherapy services through Revimo. We will ask you to give this consent in a clear, separate way (for example, by ticking a dedicated box), and you can withdraw it at any time.

Participation in testing is voluntary. Where processing is based on consent, a participant may withdraw consent at any time by contacting [privacy@revimo.eu](mailto:privacy@revimo.eu). Withdrawal does not affect processing that took place before consent was withdrawn.
        `,
      },
      {
        id: "access",
        eyebrow: "Section 5",
        title: "5. Who can access the data?",
        text: `
Personal data may be accessed by:

Authorised Revimo administrators and developers who need access for testing, support, maintenance, or security.
The relevant physiotherapist, where necessary to manage the connected patient account and assigned plans.
The relevant patient, in relation to their own account and assigned plans.
Service providers that host or operate parts of Revimo, acting only on Revimo’s instructions, such as:
- cloud hosting and database providers,
- email delivery providers,
- DNS and domain management providers,
- monitoring and logging providers.

Public authorities or other recipients where disclosure is required by law.

We do not sell personal data or use it for advertising.
        `,
      },
      {
        id: "transfers",
        eyebrow: "Section 6",
        title: "6. International data transfers",
        text: `
As far as we currently know, the service providers used for this testing phase process data within the European Economic Area.
If a service provider processes personal data outside the European Economic Area, Revimo will use an appropriate GDPR transfer mechanism, such as an adequacy decision or standard contractual clauses, where required.
       `,
      },
      {
        id: "retention",
        eyebrow: "Section 7",
        title: "7. How long we keep the data",
        text: `
Because this is a temporary test, we will keep personal data only for as long as necessary for the testing phase and for handling related technical or legal matters.

Unless a longer period is required by law:

Test accounts and associated platform data will be deleted or anonymised within 360 days after the testing phase ends or after the participant requests deletion.
Security and technical logs will be retained for 360 days.
Backups will be deleted or overwritten within 360 days.
        `,
      },
      {
        id: "security",
        eyebrow: "Section 8",
        title: "8. Security",
        text: `
We use reasonable technical and organisational measures designed to protect personal data against unauthorised access, loss, alteration, or disclosure. These measures may include encrypted connections, access controls, restricted administrator access, secure password storage, backups, monitoring, and security logs.

No online service can guarantee absolute security. If a personal-data breach occurs, Revimo will respond in accordance with its obligations under applicable data-protection law.
        `,
      },
      {
        id: "rights",
        eyebrow: "Section 9",
        title: "9. Your rights",
        text: `
Subject to applicable legal conditions, you may have the right to:

Ask whether we process your personal data.
Request access to your personal data.
Request correction of inaccurate or incomplete data.
Request deletion of your data.
Request restriction of processing.
Object to certain processing.
Request data portability where applicable.
Withdraw consent where processing is based on consent.
Lodge a complaint with the Slovenian supervisory authority, the Information Commissioner of the Republic of Slovenia.

You can exercise your rights by contacting [info@revimo.eu](mailto:info@revimo.eu). We may need to verify your identity before fulfilling a request.

The Slovenian supervisory authority is:

Information Commissioner of the Republic of Slovenia
Dunajska cesta 22, 1000 Ljubljana, Slovenia
Website: [Information Commissioner of the Republic of Slovenia](https://www.ip-rs.si/en/)
Email: gp.ip@ip-rs.si[ip-rs](https://www.ip-rs.si/en/data-protection/)
        `,
      },
      {
        id: "changes",
        eyebrow: "Section 10",
        title: "10. Changes to this notice",
        text: `
We may update this privacy notice when the testing phase, the data collected, or the way Revimo operates changes. The latest version will be published at [https://www.revimo.eu/en/privacy-policy](https://www.revimo.eu/en/privacy-policy).
        `,
      },
    ],
  },

  sl: {
    heroTitle: "Obvestilo o zasebnosti Revimo — testna faza",
    heroLead:
      "To obvestilo o zasebnosti pojasnjuje, kako Revimo med začasnim testiranjem platforme za fizioterapijo obdeluje vaše osebne podatke. Revimo je trenutno v fazi testiranja in ni namenjen kot zamenjava za strokovni zdravniški nasvet ali kot popoln sistem za vodenje zdravstvene dokumentacije.",
    lastUpdatedLabel: "Nazadnje posodobljeno:",
    lastUpdatedValue: "13. 8. 2026",
    sections: [
      {
        id: "controller",
        eyebrow: "Oddelek 1",
        title: "1. Kdo je odgovoren za vaše podatke?",
        text: `
Upravljavec osebnih podatkov je:

Revimo
E-pošta: [info@revimo.eu](mailto:info@revimo.eu)

Za vprašanja glede zasebnosti ali uveljavljanje pravic se obrnite na [privacy@revimo.eu](mailto:privacy@revimo.eu).
        `,
      },
      {
        id: "data",
        eyebrow: "Oddelek 2",
        title: "2. Katere podatke zbiramo",
        text: `
1. Pacienti

Med testiranjem lahko pacienti posredujejo ali ustvarijo:

E-poštni naslov, ki mora biti veljaven za dostop do računa in preverjanje.
Prikazno ime ali vzdevek. Pacient lahko uporablja vzdevek ali izmišljeno ime, če resnično ime ni potrebno za test.
Podatke za prijavo in overjanje računa.
Fizioterapevtske načrte, vaje, naloge, podatke o napredku ali druge informacije, ki jih pacient vnese v platformo ali jih platforma ustvari.
Informacije o tem, kateri fizioterapevt je povezan s pacientovim računom ali mu je dodelil načrt.
Zapis o dejanjih, ki so bila izvedena v platformi, na primer ustvarjeni, dodeljeni, ogledani, urejeni ali dokončani načrti.
Tehnične informacije, ki so potrebne za delovanje in varovanje storitve, kot so časovni žigi, naslov IP, podatki o brskalniku oziroma napravi ter dnevniške datoteke napak ali varnostnih dogodkov, kadar jih zbiramo.

Pacienti naj ne vnašajo resničnih zdravstvenih podatkov, diagnoz, informacij o poškodbah, fotografij ali drugih občutljivih podatkov, razen če jih Revimo k temu izrecno pozove za potrebe testiranja.

2. Fizioterapevti

Fizioterapevti lahko posredujejo:

Resnično ime in priimek.
Službeni e-poštni naslov.
Uporabniško ime.
Podatke za prijavo in overjanje.
Zapis o načrtih, ki jih fizioterapevt ustvari, dodeli, ureja ali vodi.
Informacije, ki kažejo, kateri načrti so bili dodeljeni posameznemu pacientovemu računu.
Tehnične, varnostne in aktivnosti dnevniške podatke, ki so potrebni za delovanje in zaščito platforme.

Telefonska številka v trenutni testni fazi ni zahtevana.
        `,
      },
      {
        id: "use",
        eyebrow: "Oddelek 3",
        title: "3. Kako uporabljamo podatke",
        text: `
Zbrane podatke uporabljamo za:

Ustvarjanje in upravljanje testnih računov.
Overjanje uporabnikov ter omogočanje dostopa do Revimo.
Omogočanje fizioterapevtom, da ustvarjajo in dodeljujejo fizioterapevtske načrte.
Omogočanje pacientom, da si ogledajo in izvedejo dodeljene načrte.
Beleženje aktivnosti, ki je potrebno za razumevanje uporabe platforme in ohranjanje natančne zgodovine dejanj.
Testiranje, odpravljanje napak, zagotavljanje varnosti in izboljševanje Revimo.
Komuniciranje z uporabniki glede testne faze, dostopa do računov ali pomembnih obvestil o storitvi.
Zaznavanje, preiskovanje in preprečevanje nepooblaščenega dostopa, zlorab ali varnostnih incidentov.
Izpolnjevanje zakonskih obveznosti, kadar je to potrebno.

Revimo trenutno ne omogoča neposrednega sporočanja med pacienti in fizioterapevti. Posledično Revimo trenutno ne zbira in ne shranjuje sporočil, ki bi bila izmenjana prek platforme.
        `,
      },
      {
        id: "legal",
        eyebrow: "Oddelek 4",
        title: "4. Pravna podlaga",
        text: `
V tej začasni testni fazi Revimo obdeluje osebne podatke na naslednjih pravnih podlagah po členu 6 GDPR:
- izvajanje pogodbe (člen 6(1)(b)), da lahko ustvarimo in upravljamo vaš Revimo račun ter zagotovimo storitve upravljanja fizioterapije, ki jih zahtevate;
- naši zakoniti interesi (člen 6(1)(f)) pri delovanju, varovanju in izboljševanju Revimo, vključno z beleženjem dostopov, zaznavanjem zlorab in izvajanjem tehničnih testov; ter
- vaša privolitev (člen 6(1)(a)), kadar se odločite za prejemanje neobveznih obvestil ali ko vas prosimo za dodatno soglasje za uporabe, ki niso potrebne za osnovno storitev.

Če se odločite vnesti informacije, ki razkrivajo vaše zdravstveno stanje (na primer podatke o poškodbi ali opombe k načrtu zdravljenja), te podatke obdelujemo na podlagi vaše izrecne privolitve po členu 9(2)(a) GDPR, in sicer za jasno določen namen zagotavljanja in dokumentiranja fizioterapevtskih storitev prek Revimo. To privolitev boste podali na jasen in ločen način (na primer s potrditvenim poljem), svojo privolitev pa lahko kadarkoli prekličete.

Sodelovanje v testiranju je prostovoljno. Kadar je obdelava podatkov utemeljena na privolitvi, lahko udeleženec privolitev kadarkoli prekliče tako, da kontaktira [privacy@revimo.eu](mailto:privacy@revimo.eu). Preklic privolitve ne vpliva na obdelavo, ki je bila izvedena pred preklicem.
        `,
      },
      {
        id: "access",
        eyebrow: "Oddelek 5",
        title: "5. Kdo ima dostop do podatkov?",
        text: `
Dostop do osebnih podatkov lahko imajo:

Pooblaščeni skrbniki in razvijalci Revimo, ki podatke potrebujejo za testiranje, podporo, vzdrževanje ali varnost.
Pristojni fizioterapevt, kadar je to potrebno za upravljanje povezanega pacientovega računa in dodeljenih načrtov.
Pristojni pacient, v povezavi z njegovim lastnim računom in dodeljenimi načrti.
Ponudniki storitev, ki gostijo ali zagotavljajo delovanje Revimo ter delujejo izključno po navodilih Revimo, na primer:
- ponudniki gostovanja v oblaku in podatkovnih baz,
- ponudniki pošiljanja e-pošte,
- ponudniki DNS in upravljanja domen,
- ponudniki nadzora delovanja in beleženja dnevniških zapisov.

Državni organi ali drugi prejemniki, kadar je razkritje zahtevano z zakonom.

Osebnih podatkov ne prodajamo in jih ne uporabljamo za oglaševanje.
        `,
      },
      {
        id: "transfers",
        eyebrow: "Oddelek 6",
        title: "6. Mednarodni prenosi podatkov",
        text: `
Po naših trenutnih informacijah ponudniki storitev, ki jih uporabljamo v tej testni fazi, podatke obdelujejo znotraj Evropskega gospodarskega prostora.
Če ponudnik storitev obdeluje osebne podatke zunaj Evropskega gospodarskega prostora, bo Revimo uporabil ustrezen mehanizem prenosa po GDPR, na primer sklep o ustreznosti ali standardne pogodbene klavzule, kadar je to potrebno.
        `,
      },
      {
        id: "retention",
        eyebrow: "Oddelek 7",
        title: "7. Kako dolgo hranimo podatke",
        text: `
Ker gre za začasno testiranje, bomo osebne podatke hranili le toliko časa, kot je potrebno za testno fazo in za obravnavo povezanih tehničnih ali pravnih vprašanj.

Razen če zakon zahteva daljše obdobje:

Testni računi in z njimi povezani podatki v platformi bodo izbrisani ali anonimizirani v roku 360 dni po zaključku testne faze ali po zahtevi udeleženca za izbris.
Varnostne in tehnične dnevniške datoteke bodo hranjene 360 dni.
Varnostne kopije bodo izbrisane ali prepisane v roku 360 dni.
        `,
      },
      {
        id: "security",
        eyebrow: "Oddelek 8",
        title: "8. Varnost",
        text: `
Uporabljamo ustrezne tehnične in organizacijske ukrepe za zaščito osebnih podatkov pred nepooblaščenim dostopom, izgubo, spremembo ali razkritjem. Ti ukrepi lahko vključujejo šifrirane povezave, nadzor dostopa, omejen skrbniški dostop, varno hrambo gesel, varnostne kopije, nadzor delovanja in varnostne dnevniške zapise.

Nobena spletna storitev ne more zagotoviti popolne varnosti. Če pride do kršitve varnosti osebnih podatkov, bo Revimo ukrepal v skladu z obveznostmi, ki jih določa veljavna zakonodaja o varstvu podatkov.
        `,
      },
      {
        id: "rights",
        eyebrow: "Oddelek 9",
        title: "9. Vaše pravice",
        text: `
Glede na veljavne zakonske pogoje imate lahko naslednje pravice:

Pravico zahtevati informacijo, ali vaše osebne podatke obdelujemo.
Pravico do dostopa do svojih osebnih podatkov.
Pravico zahtevati popravek netočnih ali nepopolnih podatkov.
Pravico zahtevati izbris svojih podatkov.
Pravico zahtevati omejitev obdelave.
Pravico do ugovora določeni obdelavi.
Pravico do prenosljivosti podatkov, kjer je to primerno.
Pravico do preklica privolitve, kadar je obdelava podatkov utemeljena na privolitvi.
Pravico vložiti pritožbo pri Informacijskem pooblaščencu Republike Slovenije.

Svoje pravice lahko uveljavljate tako, da kontaktirate [info@revimo.eu](mailto:info@revimo.eu). Pred izpolnitvijo zahteve vas lahko prosimo za dodatne podatke za preverjanje identitete.

Informacijski pooblaščenec Republike Slovenije je:

Informacijski pooblaščenec Republike Slovenije
Dunajska cesta 22, 1000 Ljubljana, Slovenija
Spletna stran: [Informacijski pooblaščenec Republike Slovenije](https://www.ip-rs.si/en/)
E-pošta: gp.ip@ip-rs.si[ip-rs](https://www.ip-rs.si/en/data-protection/)
        `,
      },
      {
        id: "changes",
        eyebrow: "Oddelek 10",
        title: "10. Spremembe tega obvestila",
        text: `
To obvestilo o zasebnosti lahko posodobimo, kadar se spremeni testna faza, obseg zbranih podatkov ali način delovanja Revimo. Najnovejša različica bo objavljena na [https://www.revimo.eu/sl/privacy-policy](https://www.revimo.eu/sl/privacy-policy).
        `,
      },
    ],
  },
} as const;